import { configuredFetchBaseQuery } from '@config/rtkQuery';
import {
  toastFulfilledPromise,
  toastPendingPromise,
  toastRejectedPromise,
} from '@features/promiseHandlers';
import { createSlice } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/dist/query/react';

export const capacityApi = createApi({
  reducerPath: 'capacityApi',
  baseQuery: configuredFetchBaseQuery(),
  endpoints: (builder) => ({
    getUserTrunks: builder.query({
      query: (arg) => {
        const url = 'customer/trunks';
        const { page, size, ...params } = arg;
        return { url, params: { page, page_size: size, ...params } };
      },
      providesTags: ['CustomerTrunk'],
    }),
    getUserTrunksForSelect: builder.query({
      query: () => ({ url: 'customer/trunks/for-select' }),
    }),
    getUserTrunkDetails: builder.query({
      query: (arg) => {
        const { trunkID } = arg;
        return `customer/trunks/${trunkID}`;
      },
      providesTags: (result, error, { trunkID }) => [
        { tag: 'CustomerTrunk', type: trunkID },
      ],
    }),
    createUserTrunk: builder.mutation({
      query: ({ data: body }) => ({
        url: `customer/trunks`,
        method: 'POST',
        body: { name: body.name, type_id: body.type, prefix: body.prefix },
      }),
      invalidatesTags: ['CustomerTrunk'],
    }),
    updateUserTrunk: builder.mutation({
      query: ({ trunkID, data: body }) => ({
        url: `customer/trunks/${trunkID}`,
        method: 'PUT',
        body: { name: body.name, type_id: body.type, prefix: body.prefix },
      }),
      invalidatesTags: (result, error, { trunkID }) => [
        'CustomerTrunk',
        { tag: 'CustomerTrunk', type: trunkID },
      ],
    }),
    getUserTrunkAddresses: builder.query({
      query: (arg) => {
        const { trunkID } = arg;
        return `customer/trunks/${trunkID}/ip-addresses`;
      },
      providesTags: (result, error, { trunkID }) => [
        { tag: 'TrunkAddress', type: trunkID },
      ],
    }),
    deleteUserTrunkAddress: builder.mutation({
      query: ({ id, trunkID }) => ({
        url: `customer/trunks/${trunkID}/ip-addresses/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { trunkID }) => [
        { tag: 'TrunkAddress', type: trunkID },
      ],
    }),
    createUserTrunkAddress: builder.mutation({
      query: ({ trunkID, data: body }) => ({
        url: `customer/trunks/${trunkID}/ip-addresses`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { trunkID }) => [
        { tag: 'TrunkAddress', type: trunkID },
      ],
    }),
    updateUserTrunkAddress: builder.mutation({
      query: ({ trunkID, data: body }) => ({
        url: `customer/trunks/${trunkID}/ip-addresses/update`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { trunkID }) => [
        { tag: 'TrunkAddress', type: trunkID },
      ],
    }),
    getDedicatedChannelsDids: builder.query({
      query: (arg) => {
        const url = 'customer/dids/dedicated-channels/existing';
        const { page, size, ...params } = arg;
        return { url, params: { page, page_size: size, ...params } };
      },
      providesTags: ['DedicatedChannelsDids'],
      transformResponse: (response) => {
        const { results } = response;
        results.forEach((item) => {
          // Defaults
          item.canAdd = item.cancellation_cart_channels > 0;
          item.canRemove = false;

          if (item.cancellation_cart_channels === item.n_bought_channels) {
            item.canRemove = false;
            return;
          }
          if (item.cancellation_cart_channels < item.n_bought_channels) {
            item.canRemove = true;
          }
        });
        return response;
      },
    }),
    getDidsForDedicatedChannelsOrder: builder.query({
      query: (arg) => {
        const url = 'customer/dids/dedicated-channels/available';
        const { page, size, ...params } = arg;
        return { url, params: { page, page_size: size, ...params } };
      },
      providesTags: (result, error, { login }) => [
        { tag: 'DedicatedChannelsDidsOrder', type: login },
      ],
      transformResponse: (response) => {
        const { results } = response;
        results.forEach((item) => {
          // Defaults
          item.canAdd = false;
          item.canRemove = item.cart_channels > 0;

          if (item.cart_channels + item.n_channels >= item.max_n_channels) {
            item.canAdd = false;
            return;
          }
          if (item.cart_channels + item.n_channels < item.max_n_channels) {
            item.canAdd = true;
          }
        });
        return response;
      },
    }),
  }),
});

const initialState = {
  toast: {},
};

const capacitySlice = createSlice({
  name: 'capacity',
  initialState,
  reducers: {},
  extraReducers: (builder) =>
    builder
      .addMatcher(
        capacityApi.endpoints.updateUserTrunkAddress.matchPending,
        (state, action) =>
          toastPendingPromise(
            state,
            action,
            'Saving existing trunk addresses...'
          )
      )
      .addMatcher(
        capacityApi.endpoints.updateUserTrunkAddress.matchFulfilled,
        (state, action) =>
          toastFulfilledPromise(state, action, {
            render: 'Trunk address is saved.',
          })
      )
      .addMatcher(
        capacityApi.endpoints.updateUserTrunkAddress.matchRejected,
        toastRejectedPromise
      )
      .addMatcher(
        capacityApi.endpoints.createUserTrunkAddress.matchPending,
        (state, action) =>
          toastPendingPromise(state, action, 'Saving new trunk addresses...')
      )
      .addMatcher(
        capacityApi.endpoints.createUserTrunkAddress.matchFulfilled,
        (state, action) =>
          toastFulfilledPromise(state, action, {
            render: 'Trunk address is saved.',
          })
      )
      .addMatcher(
        capacityApi.endpoints.createUserTrunkAddress.matchRejected,
        toastRejectedPromise
      )
      .addMatcher(
        capacityApi.endpoints.createUserTrunk.matchPending,
        (state, action) =>
          toastPendingPromise(state, action, 'Saving new trunk...')
      )
      .addMatcher(
        capacityApi.endpoints.createUserTrunk.matchFulfilled,
        (state, action) =>
          toastFulfilledPromise(state, action, { render: 'Trunk is saved.' })
      )
      .addMatcher(
        capacityApi.endpoints.createUserTrunk.matchRejected,
        toastRejectedPromise
      )
      .addMatcher(
        capacityApi.endpoints.updateUserTrunk.matchPending,
        (state, action) =>
          toastPendingPromise(state, action, 'Saving new trunk...')
      )
      .addMatcher(
        capacityApi.endpoints.updateUserTrunk.matchFulfilled,
        (state, action) =>
          toastFulfilledPromise(state, action, { render: 'Trunk is saved.' })
      )
      .addMatcher(
        capacityApi.endpoints.updateUserTrunk.matchRejected,
        toastRejectedPromise
      )
      .addMatcher(
        capacityApi.endpoints.deleteUserTrunkAddress.matchPending,
        (state, action) =>
          toastPendingPromise(state, action, 'Deleting trunk...')
      )
      .addMatcher(
        capacityApi.endpoints.deleteUserTrunkAddress.matchFulfilled,
        (state, action) =>
          toastFulfilledPromise(state, action, { render: 'Trunk deleted.' })
      )
      .addMatcher(
        capacityApi.endpoints.deleteUserTrunkAddress.matchRejected,
        toastRejectedPromise
      ),
});

export default capacitySlice.reducer;
